import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import {
  CAR_STATUS_LABEL,
  CLASSIFICATION_LABEL,
  Pill,
  carTone,
  classificationTone,
} from '../../components/audit/AuditVocabulary.js';
import { fetchAuditReport, type AuditReportDocument } from '../../lib/audit-api.js';
import { ApiError } from '../../lib/api.js';
import { downloadDocument, issueAuditReport } from '../../lib/documents-api.js';
import { t, formatDate, formatDateTime } from '../../lib/i18n.js';

/**
 * The auto-generated Audit Report preview (spec §11 key screen 7).
 *
 * The word in the spec is **preview**, and this screen takes it literally: it
 * shows what the report says, composed afresh from captured data. Once the
 * audit is closed, the lead auditor can **issue** it: the same report rendered
 * to PDF and kept as the document of record. A provisional report cannot be
 * issued. Distributing it is a later pass, so nothing here claims it was sent.
 *
 * Three display rules carry the weight, and all three exist because an audit
 * report is the document that leaves the building:
 *
 *  1. **A provisional report says so at the top, before anything else.** The
 *     failure mode of every auto-generated report is a draft that looks final.
 *  2. **Caveats come before the findings, not in a footnote.** A reader who
 *     stops halfway must have already been told what is missing.
 *  3. **An empty section renders its own sentence rather than vanishing.** A
 *     missing section makes "nothing was found" and "nobody looked" identical.
 */
export function AuditReport() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const templateId = params.get('templateId') ?? undefined;

  const query = useQuery({
    queryKey: ['audit-report', id, templateId],
    queryFn: () => fetchAuditReport(id!, templateId),
    enabled: Boolean(id),
    // A report is a point-in-time document. Serving a cached one after somebody
    // has verified a corrective action would show a reader a state that is no
    // longer true, on the one screen where that matters most.
    staleTime: 0,
  });

  const report = query.data?.report;

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audits', report?.referenceNo ?? '…', 'Report']} />

      <main className="mx-auto max-w-4xl px-4 pb-12 pt-4">
        {query.isLoading && <p className="text-content-secondary">{t("Composing the report…")}</p>}
        {query.error && (
          <p className="text-tone-critical">
            {t("That report could not be generated. You may not have export authority for this audit.")}
          </p>
        )}

        {report && (
          <article>
            <ProvisionalBanner report={report} />

            <header className="mt-6">
              <h1 className="m-0 text-2xl font-semibold">{report.title}</h1>
              <p className="mt-1 text-sm text-content-secondary">
                {report.referenceNo}{' '}{t("· generated")}{' '}
                {formatDateTime(report.generatedAt)}
              </p>
            </header>

            <IssuePanel
              auditId={id!}
              templateId={templateId}
              referenceNo={report.referenceNo}
              provisional={report.isProvisional}
              issued={query.data?.issued ?? null}
            />

            <Caveats caveats={report.caveats} />

            <Summary report={report} />

            <Section report={report} sectionKey="scope">
              {report.plan ? (
                <dl className="grid gap-2 text-sm">
                  <Row label={t("Plan")}>{report.plan.title}</Row>
                  <Row label={t("Scope")}>{report.plan.scope ?? '—'}</Row>
                  <Row label={t("Criteria")}>{report.plan.criteria ?? '—'}</Row>
                  <Row label={t("Location")}>{report.plan.location ?? '—'}</Row>
                  <Row label={t("Shared with owners")}>
                    {report.plan.sharedAt
                      ? formatDate(report.plan.sharedAt)
                      : t("Not shared")}
                  </Row>
                </dl>
              ) : null}
            </Section>

            <Section report={report} sectionKey="meetings">
              <dl className="grid gap-2 text-sm">
                <Row label={t("Lead auditor")}>{report.audit.leadAuditorName ?? '—'}</Row>
                <Row label={t("Team")}>
                  {report.audit.teamNames.length > 0 ? report.audit.teamNames.join(', ') : '—'}
                </Row>
                <Row label={t("Opening meeting")}>
                  {report.audit.openingMeetingAt
                    ? formatDateTime(report.audit.openingMeetingAt)
                    : t("Not recorded")}
                </Row>
                {report.audit.openingMeetingMinutes && (
                  <Row label={t("Opening minutes")}>{report.audit.openingMeetingMinutes}</Row>
                )}
                <Row label={t("Closing meeting")}>
                  {report.audit.closingMeetingAt
                    ? formatDateTime(report.audit.closingMeetingAt)
                    : t("Not recorded")}
                </Row>
                {report.audit.closingMeetingMinutes && (
                  <Row label={t("Closing minutes")}>{report.audit.closingMeetingMinutes}</Row>
                )}
              </dl>
            </Section>

            <Section report={report} sectionKey="checklist">
              <p className="m-0 text-sm">
                {report.checklist.answered}{' '}{t("of")}{' '}{report.checklist.total}{' '}{t("items answered,")}{' '}
                {report.checklist.nonConforming}{' '}{t("non-conforming.")}
              </p>
            </Section>

            <Section report={report} sectionKey="evidence">
              <ul className="m-0 list-none space-y-2 p-0 text-sm">
                {report.evidence.map((item, index) => (
                  <li key={index} className="rounded border border-border p-3">
                    <span className="text-content-secondary">{item.evidenceType}</span> —{' '}
                    {item.description ?? t("No description recorded.")}
                    {item.gpsCaptureStatus !== 'captured' && (
                      <span className="ms-2 text-xs text-content-secondary">
                        {t("(location not captured:")}{' '}{item.gpsCaptureStatus.replace('_', ' ')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>

            <Section report={report} sectionKey="findings">
              <ul className="m-0 list-none space-y-4 p-0">
                {report.findings.map((finding) => (
                  <li
                    key={finding.referenceNo}
                    className="rounded-md border border-border p-4 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <strong>{finding.referenceNo}</strong>
                      <span className="flex gap-2">
                        <Pill tone={classificationTone(finding.classification)}>
                          {t(CLASSIFICATION_LABEL[finding.classification])}
                        </Pill>
                        <Pill tone={finding.closedAt ? 'positive' : 'warning'}>
                          {finding.closedAt ? t("Closed") : t("Open")}
                        </Pill>
                      </span>
                    </div>
                    {finding.clause && (
                      <p className="mt-1 mb-0 text-content-secondary">{t("Clause")}{' '}{finding.clause}</p>
                    )}
                    <p className="mt-2 mb-0">{t(finding.description)}</p>
                    {finding.riskLevel && (
                      <p className="mt-1 mb-0 text-content-secondary">{t("Risk:")}{' '}{finding.riskLevel}</p>
                    )}

                    {finding.correctiveActions.length > 0 && (
                      <ul className="mt-3 list-none space-y-2 p-0">
                        {finding.correctiveActions.map((car) => (
                          <li
                            key={car.referenceNo}
                            className="rounded border border-border p-3 text-xs"
                          >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <span>
                                <strong>{car.referenceNo}</strong>
                                {car.departmentName ? ` · ${car.departmentName}` : ''}
                                {car.ownerName ? ` · ${car.ownerName}` : ''}
                              </span>
                              <Pill tone={carTone(car.status as never)}>
                                {CAR_STATUS_LABEL[car.status as never] ?? car.status}
                              </Pill>
                            </div>
                            {car.verificationNotes && (
                              <p className="mt-1 mb-0">{car.verificationNotes}</p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </Section>

            {/* The detail sits under each finding above. This section still
                exists so an audit with no findings at all says something about
                corrective actions rather than nothing — which is the composer's
                rule, and dropping it here would quietly break it. */}
            <Section report={report} sectionKey="corrective-actions">
              <p className="m-0 text-sm">
                {report.counts.cars}{' '}{t("corrective action")}{report.counts.cars === 1 ? '' : 's'}{' '}
                {t("issued,")}{' '}{report.counts.carsOpen}{' '}{t("still open. Each is listed under the finding it came from.")}
              </p>
            </Section>

            <footer className="mt-10 border-t border-border pt-4 text-sm text-content-secondary">
              <p className="m-0">
                {t("Composed from the data captured against")}{' '}{report.referenceNo}{t(". Nothing in this document is generated text — every line above is something somebody recorded.")}
              </p>
              <p className="mt-2 mb-0">
                {t("Issuing keeps this report as a PDF, the document of record. Sending it to a distribution list is not available yet, so nothing here says it was sent.")}
              </p>
              <p className="mt-2 mb-0">
                <Link to={`/audit/audits/${report.referenceNo ? id : ''}`}>
                  {t("Back to the audit")}
                </Link>
              </p>
            </footer>
          </article>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProvisionalBanner({ report }: { report: AuditReportDocument }) {
  if (!report.isProvisional) return null;

  return (
    <div
      role="status"
      className="mt-4 rounded-md border border-border bg-tone-info-surface p-4"
    >
      <p className="m-0 text-sm font-semibold">{t("Provisional — this audit is not finished")}</p>
      <p className="mt-1 mb-0 text-sm">{report.provisionalReason}</p>
    </div>
  );
}

function Caveats({ caveats }: { caveats: string[] }) {
  if (caveats.length === 0) return null;

  return (
    <section className="mt-6 rounded-md border border-border bg-tone-critical-surface p-4">
      <h2 className="m-0 text-base font-semibold">{t("Before you rely on this report")}</h2>
      <ul className="mt-2 mb-0 list-disc space-y-1 ps-5 text-sm">
        {caveats.map((caveat) => (
          <li key={caveat}>{t(caveat)}</li>
        ))}
      </ul>
    </section>
  );
}

function Summary({ report }: { report: AuditReportDocument }) {
  return (
    <section className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
      {[
        { label: 'Findings', value: report.counts.findings, note: `${report.counts.findingsOpen} open` },
        {
          label: 'Corrective actions',
          value: report.counts.cars,
          note: `${report.counts.carsOpen} open`,
        },
        {
          label: 'Checklist',
          value: report.checklist.answered,
          note: `of ${report.checklist.total} answered`,
        },
        { label: 'Evidence', value: report.evidence.length, note: 'items recorded' },
      ].map((tile) => (
        <div key={t(tile.label)} className="rounded-md border border-border bg-surface p-4">
          <p className="m-0 text-xs text-content-secondary">{t(tile.label)}</p>
          <p className="m-0 mt-1 text-2xl font-semibold">{tile.value}</p>
          <p className="m-0 mt-1 text-xs text-content-secondary">{tile.note}</p>
        </div>
      ))}
    </section>
  );
}

function Section({
  report,
  sectionKey,
  children,
}: {
  report: AuditReportDocument;
  sectionKey: string;
  children: React.ReactNode;
}) {
  const section = report.sections.find((entry) => entry.key === sectionKey);
  if (!section) return null;

  return (
    <section className="mt-8">
      <h2 className="m-0 text-lg font-semibold">{t(section.heading)}</h2>
      {section.absence ? (
        /* The section stays, and says why it is empty. Hiding it would make
           "nothing was found" and "nobody looked" indistinguishable. */
        <p className="mt-2 mb-0 text-sm text-content-secondary">{t(section.absence)}</p>
      ) : (
        <div className="mt-3">{children}</div>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2">
      <dt className="text-content-secondary">{label}</dt>
      <dd className="m-0">{children}</dd>
    </div>
  );
}

/**
 * Issuing: the preview above becomes a kept PDF. Only a closed audit's report
 * can be issued; the button says why when it cannot.
 */
function IssuePanel({
  auditId,
  templateId,
  referenceNo,
  provisional,
  issued,
}: {
  auditId: string;
  templateId: string | undefined;
  referenceNo: string;
  provisional: boolean;
  issued: { id: string; title: string; createdAt: string } | null;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const issue = async () => {
    setBusy(true);
    setError(null);
    try {
      const { document } = await issueAuditReport(auditId, templateId);
      await queryClient.invalidateQueries({ queryKey: ['audit-report', auditId] });
      await downloadDocument(document.id, `${referenceNo}.pdf`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("The report could not be issued."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-6 rounded-md border border-border p-4 text-sm" aria-label={t("Issued report")} data-testid="issue-panel">
      {issued ? (
        <p className="m-0">
          {t("Issued")}{' '}{formatDateTime(issued.createdAt)}.{' '}
          <button
            type="button"
            onClick={() => void downloadDocument(issued.id, `${referenceNo}.pdf`).catch(() => setError(t("The issued report could not be opened.")))}
            className="text-ag-purple underline"
          >
            {t("Download the issued report (PDF)")}
          </button>
        </p>
      ) : (
        <p className="m-0 text-content-secondary">{t("Not issued yet. What you see is a preview.")}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={busy || provisional}
          onClick={() => void issue()}
          className="rounded-sm bg-ag-purple px-4 py-2 font-medium text-content-inverse disabled:opacity-50"
        >
          {busy ? t("Issuing…") : issued ? t("Issue again from this preview") : t("Issue the report (PDF)")}
        </button>
        {provisional && <span className="text-xs text-content-secondary">{t("A provisional report cannot be issued. Close the audit first.")}</span>}
      </div>
      {error && (
        <p role="alert" className="mb-0 mt-2" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </section>
  );
}
